import { type BaseItem } from './common.js';
import { AppointmentStatus, type Encounter } from './encounter.js';
import type * as Patient from './patient.js';
import { type LocalizedPreference } from './practice/preference.js';
import type * as Practice from './practice.js';
import type * as ProviderProfile from './providerProfile.js';
import type * as User from './user.js';

export enum IneligibilityReasons {
  IncompletePrevisitFormset = 'INCOMPLETE_PREVISIT_FORMSET',
  InsuranceChanges = 'INSURANCE_CHANGES',
  InsuranceVerifySourceNotSupported = 'INSURANCE_VERIFY_SOURCE_NOT_SUPPORTED',
  NoPhotoIdForPatient = 'NO_PHOTO_ID_FOR_PATIENT',
  OutstandingCoPayPayments = 'OUTSTANDING_CO_PAY_PAYMENTS',
  /**
   * @deprecated OutstandingPayments in favor of OutstandingCoPayPayments and OutstandingPrePayPayments
   */
  OutstandingPayments = 'OUTSTANDING_PAYMENTS',
  OutstandingPrePayPayments = 'OUTSTANDING_PRE_PAY_PAYMENTS',
  PatientCareProviderUpdated = 'PATIENT_CARE_PROVIDER_UPDATED',
  EncounterPayorIsIneligible = 'ENCOUNTER_PAYOR_IS_INELIGIBLE',
  PatientUnderAge = 'PATIENT_UNDERAGE',
  SameDayAppointmentsIneligibility = 'SAME_DAY_APPOINTMENTS_INELIGIBILITY',
  VisitReasonNotSupported = 'VISIT_REASON_NOT_SUPPORTED',
}

export interface BaseDigitalCheckInEncounter {
  encounterId: string;
  encounterEligibility: boolean | null;
  ineligibilityReasons: IneligibilityReasons[] | null;
  notificationDeliveredTimestamp: Date | null;
  notificationUndeliveredTimestamp: Date | null;
  checkinTimestamp: Date | null;
}
export interface DigitalCheckInEncounter extends BaseDigitalCheckInEncounter, BaseItem {}

export enum DigitalCheckInFailureReason {
  AppointmentIsCheckedIn = 'APPOINTMENT_IS_CHECKED_IN',
  AppointmentNotFound = 'APPOINTMENT_NOT_FOUND',
  AuthCodeExpired = 'AUTH_CODE_EXPIRED',
  DigitalCheckInEncounterNotFound = 'DIGITAL_CHECK_IN_ENCOUNTER_NOT_FOUND',
  EncounterEhrIdentifierNotDefined = 'ENCOUNTER_EHR_IDENTIFIER_NOT_DEFINED',
  FailedToUpdateAppointmentStatus = 'FAILED_TO_UPDATE_APPOINTMENT_STATUS',
  FHIRAppointmentLocked = 'FHIR_APPOINTMENT_LOCKED',
  FHIRUnprocessableEntity = 'FHIR_UNPROCESSABLE_ENTITY',
  IneligibleForDigitalCheckIn = 'INELIGIBLE_FOR_DIGITAL_CHECK_IN',
  PatientUnauthorizedForDigitalCheckIn = 'PATIENT_UNAUTHORIZED_FOR_DIGITAL_CHECK_IN',
  Unknown = 'UNKNOWN', // This is a catch-all for any other error
}

export const digitalCheckInFailureReasonMap: Record<DigitalCheckInFailureReason, string> = {
  [DigitalCheckInFailureReason.AppointmentIsCheckedIn]: 'Appointment is already checked in',
  [DigitalCheckInFailureReason.AppointmentNotFound]: 'Appointment not found',
  [DigitalCheckInFailureReason.AuthCodeExpired]: 'Auth code is expired',
  [DigitalCheckInFailureReason.DigitalCheckInEncounterNotFound]:
    'Digital check-in encounter not found',
  [DigitalCheckInFailureReason.EncounterEhrIdentifierNotDefined]:
    'Encounter EHR identifier not defined',
  [DigitalCheckInFailureReason.FailedToUpdateAppointmentStatus]:
    'Failed to update appointment status',
  [DigitalCheckInFailureReason.FHIRAppointmentLocked]: 'FHIR - Appointment Locked',
  [DigitalCheckInFailureReason.FHIRUnprocessableEntity]: 'FHIR - Unprocessable Entity',
  [DigitalCheckInFailureReason.IneligibleForDigitalCheckIn]: 'Ineligible for Digital check-in',
  [DigitalCheckInFailureReason.PatientUnauthorizedForDigitalCheckIn]:
    'Patient Unauthorized for Digital check-in',
  [DigitalCheckInFailureReason.Unknown]: 'Unknown',
};

export const FINISHED_CHECK_IN_APPOINTMENT_STATUS = [
  AppointmentStatus.Arrived,
  AppointmentStatus.CheckedIn,
];

export type DigitalCheckInAppointmentStatus =
  AppointmentStatus.Arrived | AppointmentStatus.CheckedIn;

export interface DigitalCheckInDetails {
  appointmentStatus: AppointmentStatus | null;
  encounter: Pick<
    Encounter,
    'arrivalTime' | 'startTime' | 'patientDirections' | 'patientInstructions'
  >;
  patient: Pick<Patient.Patient, 'firstName' | 'preferredFirstName' | 'preferredLanguage'>;
  provider: Pick<User.User, 'firstName' | 'lastName' | 'suffix' | 'previsitImageUrl' | 'prefix'> & {
    specialties: NonNullable<ProviderProfile.Profile['specialties']> | [];
  };
  practice: Pick<Practice.Practice, 'name' | 'address' | 'city' | 'state' | 'zip'>;
  practicePreference: {
    digitalArrivalCustomContent: Record<string, string>;
    digitalArrivalIdentificationConfirmationRequired: boolean;
    digitalCheckInCustomArrivedContent: LocalizedPreference;
  };
}
